import ActionBarItem from '@rio-cloud/rio-uikit/ActionBarItem';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import ReleaseNotes from '@rio-cloud/rio-uikit/ReleaseNotes';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { config } from '../../../config';
import { RELEASE_NOTES_RELEASE_DATE_ID, shouldShowReleaseNotes } from './releaseNotesHelper';

const RELEASE_NOTES_ITEM = 'asset-administration.release-notes';
const CURRENT_VERSION = config.serviceVersion;
const CURRENT_VERSION_NUMBERS = CURRENT_VERSION.split('.');
const MODULE_NAME_ID = 'assets.moduleName';
const WHATS_NEW_ID = 'assets.release-notes.whats-new';
const CLOSE_ID = 'assets.release-notes.close';
const RELEASE_NOTES_ENTRY_PREFIX = `assets.release-notes.entry.${CURRENT_VERSION}.`;
const RELEASE_NOTES_INTRO_ID = `${RELEASE_NOTES_ENTRY_PREFIX}intro`;

const getReleaseNotesEntryIds = (messages: Record<string, string>) => {
    return Object.keys(messages)
        .filter(
            messageId =>
                messageId.startsWith(RELEASE_NOTES_ENTRY_PREFIX) &&
                messageId !== RELEASE_NOTES_INTRO_ID &&
                messageId !== RELEASE_NOTES_RELEASE_DATE_ID
        )
        .sort((leftId, rightId) =>
            leftId
                .slice(RELEASE_NOTES_ENTRY_PREFIX.length)
                .localeCompare(rightId.slice(RELEASE_NOTES_ENTRY_PREFIX.length), undefined, {
                    numeric: true,
                })
        );
};

const hasUnreadReleaseNotes = (localVersion: string | null) => {
    if (!localVersion) {
        return true;
    }

    const localVersionNumbers = localVersion.split('.');

    return !(
        CURRENT_VERSION_NUMBERS[0] === localVersionNumbers[0] && CURRENT_VERSION_NUMBERS[1] === localVersionNumbers[1]
    );
};

export const ReleaseNotesActionBarItem = () => {
    const intl = useIntl();
    const [showReleaseNotes, setShowReleaseNotes] = useState(false);

    if (!shouldShowReleaseNotes(intl)) {
        return null;
    }

    const releaseNotesEntryIds = getReleaseNotesEntryIds(intl.messages as Record<string, string>);

    const moduleName = intl.formatMessage({ id: MODULE_NAME_ID });
    const whatsNewTitle = intl.formatMessage({
        id: WHATS_NEW_ID,
    });
    const closeButtonText = intl.formatMessage({
        id: CLOSE_ID,
    });
    const lastReadVersion = typeof window === 'undefined' ? null : window.localStorage.getItem(RELEASE_NOTES_ITEM);
    const hasNewReleaseNotes = hasUnreadReleaseNotes(lastReadVersion);
    const actionBarItemTitle = `${moduleName} v${CURRENT_VERSION}`;
    const closeReleaseNotes = () => {
        window.localStorage.setItem(RELEASE_NOTES_ITEM, CURRENT_VERSION);
        setShowReleaseNotes(false);
    };
    const translatedReleaseNotes = {
        [CURRENT_VERSION]: {
            content: (
                <div className='text-wrap-balance'>
                    <div className='margin-bottom-10'>
                        <p className='text-size-16'>
                            {intl.formatMessage({
                                id: RELEASE_NOTES_INTRO_ID,
                            })}
                        </p>
                    </div>
                    <ul className='margin-bottom-0 margin-left-5 padding-left-25'>
                        {releaseNotesEntryIds.map(messageId => (
                            <li key={messageId}>
                                {intl.formatMessage({
                                    id: messageId,
                                })}
                            </li>
                        ))}
                    </ul>
                </div>
            ),
            date: intl.formatDate(
                Date.parse(
                    intl.formatMessage({
                        id: RELEASE_NOTES_RELEASE_DATE_ID,
                    })
                )
            ),
        },
    };

    return (
        <>
            <ActionBarItem id='release-notes-action-bar-item'>
                <ActionBarItem.Icon>
                    <span className='icon rioglyph rioglyph-info-sign' />
                    {hasNewReleaseNotes && <span className='badge bg-primary badge-indicator'>1</span>}
                </ActionBarItem.Icon>
                <ActionBarItem.Popover title={actionBarItemTitle}>
                    <ActionBarItem.List>
                        <ActionBarItem.ListItem icon='rioglyph-info-sign' onClick={() => setShowReleaseNotes(true)}>
                            {whatsNewTitle}
                            {hasNewReleaseNotes && <span className='badge badge-primary margin-left-5'>1</span>}
                        </ActionBarItem.ListItem>
                    </ActionBarItem.List>
                </ActionBarItem.Popover>
            </ActionBarItem>

            <Dialog
                show={showReleaseNotes}
                useOverflow={true}
                showCloseButton={false}
                title={<FormattedMessage id={MODULE_NAME_ID} />}
                subtitle={<FormattedMessage id={WHATS_NEW_ID} />}
                body={<ReleaseNotes releaseNotes={translatedReleaseNotes} />}
                bodyClassName='bg-lightest'
                headerButtons={
                    <Button
                        bsStyle={Button.MUTED}
                        iconOnly
                        iconName='rioglyph-remove'
                        autoTrackingKey={`dialog::closed::${MODULE_NAME_ID}`}
                        className='modal-header-close close'
                        onClick={closeReleaseNotes}
                        data-testid='close'
                        aria-label='dialog close button'
                    />
                }
                footer={<Button onClick={closeReleaseNotes}>{closeButtonText}</Button>}
                onClose={closeReleaseNotes}
            />
        </>
    );
};
